import {Phase2Api} from "./api";
import {Button, Col, Divider, Dropdown, Icon, Input, Menu, Modal, Row, Tooltip} from "antd";
import {BASE_CLS} from "./util";
import {getSNodeID} from "../../../../components/scheduling/util";
import SelectionCount from "../../../../components/search/SelectionCount";
import React from "react";
import {Location} from "../../../../__generated__/graphql/types";
import useUserGroups from "../../../../hooks/useUserGroups";

interface BaseFeatureConfig {
    enabled: boolean
}

interface FeatureConfig {
    collapseButton?: BaseFeatureConfig,
    editMenu?: BaseFeatureConfig,
    selectMenu?: BaseFeatureConfig & {
        forceVisible?: boolean
    },
    viewMenu?: BaseFeatureConfig,
    toolsMenu?: BaseFeatureConfig,
    pilotNodeEditorButton?: BaseFeatureConfig,
    defaultChargeCode?: BaseFeatureConfig,
    editButton?: BaseFeatureConfig,
    selectionCount?: BaseFeatureConfig
}

type FeatureName = keyof FeatureConfig

function getApiContext(api: Phase2Api){
    return {
        data: api.data,
        editMode: api.editMode.get(),
        hideCarrierPanel: api.carriableSelector.hideCarriablePanel,
        selectedEntityIds: api.selection.entityIds,
        scheduledPax: api.data.modifiedEntities
            .get()
            .filter(c => c.classType === 'flytsuite.paxnode'),
        chargeCodes: api.chargeCodes,
        hasData: !!api.data.modifiedEntities.get().length,
    }
}

export const SelectMenu = React.memo<{ api: Phase2Api }>(
    function SelectMenu({ api }){
        const { hasData, selectedEntityIds } = getApiContext(api);

        function renderSelectLegMenu(){
            return <Menu.SubMenu
                key="selectleg"
                title="Select leg..."
            >
                {api.data.getLegs().map((leg) => {
                    return (
                        <Menu.Item
                            key={`${leg.departure._id}::${leg.destination._id}`}
                            onClick={() => api.selection.entityIds.add(leg.all.map(e => getSNodeID(api.toScheduleNode(e))))}
                        >
                            {leg.departure.name + ' → ' + leg.destination.name}
                        </Menu.Item>
                    )
                })}
            </Menu.SubMenu>
        }

        return <Dropdown
            trigger={['click']}
            overlay={
                <Menu>
                    <Menu.Item
                        disabled={!hasData || selectedEntityIds.get().length >= api.data.modifiedEntities.get().length}
                        key="selectall"
                        onClick={selectedEntityIds.all}
                    >Select All</Menu.Item>
                    <Menu.Item
                        disabled={!hasData || !selectedEntityIds.get().length}
                        key="deselectall"
                        onClick={selectedEntityIds.clear}
                    >De-select All</Menu.Item>
                    {renderSelectLegMenu()}
                </Menu>
            }
        >
            <Button>
                Select <Icon type="down" />
            </Button>
        </Dropdown>
    }
);

export interface ToolbarProps {
    api: Phase2Api,
    featureConfig?: FeatureConfig
}

export const ToolsMenu: React.FC<{ api: Phase2Api }> = ({ api }) => {

    const menuItems: React.ReactNode[] = [];

    if (api.editMode.get()){
        menuItems.push(
            <Menu.Item
                key="pilotNoteEditor"
                onClick={api.notesForPilot.editor.open}
            >
                Edit Pilot Notes
            </Menu.Item>
        )
    }

    menuItems.push(
        <Menu.Item
            key="purgeEntitiesFromFlightTool"
            onClick={() => api.purgeEntitiesFromFlightTool.modal.setOpen(true)}
        >
            Purge PAX/CGO from flight
        </Menu.Item>
    )

    if (!api.editMode.get()){
        menuItems.push(
            <Menu.Item
                key="scheduledToExpectedPaxCgo"
                onClick={() => api.scheduledToExpectedPaxCgo.modal.setOpen(true)}
            >
                Make PAX/CGO Expected
            </Menu.Item>
        )
    }

    return <Col>
        <Dropdown
            trigger={['click']}
            overlay={
                <Menu>
                    {menuItems}
                </Menu>
            }
        >
            <Button>
                Tools <Icon type="down" />
            </Button>
        </Dropdown>
    </Col>
}

const Toolbar: React.FC<ToolbarProps> = (props) => {

    const { api } = props;

    const [ userGroups ] = useUserGroups();

    const {
        data,
        editMode,
        hideCarrierPanel,
        selectedEntityIds,
        scheduledPax,
        chargeCodes
    } = getApiContext(api);

    function getFeatureConfig<F extends FeatureName>(featureName: F){
        return props.featureConfig[featureName] as FeatureConfig[F];
    }

    function isFeatureEnabled(featureName: FeatureName){
        return props.featureConfig[featureName]?.enabled;
    }

    const renderFeature = (featureName: FeatureName) => (component: React.ReactElement) => {
        if (isFeatureEnabled(featureName)){
            return component
        }
        return null;
    }

    let collapserIcon = 'menu-fold';
    let collapserTooltipText = 'Hide personnel search';
    if (hideCarrierPanel) {
        collapserIcon = 'menu-unfold';
        collapserTooltipText = 'Show personnel search';
    }

    function renderMoveToMenu(){

        const firstID = selectedEntityIds.get()[0];
        const firstSelectedEntity = api.data.modifiedEntities.get().find(e => getSNodeID(api.toScheduleNode(e)) === firstID);

        return <Menu.SubMenu
            title="Move to..."
            disabled={!api.selection.entityIds.get().length}
        >
            <Menu.Item
                onClick={() => {

                    const callback = (departure: Location, destination: Location) => {
                        api.data.setEntityLegs(
                            api.selection.entityIds.get(),
                            departure,
                            destination
                        )
                    }

                    // Automatically fill in the departure and destination from
                    // the first selected entity.
                    if (firstSelectedEntity) {
                        api.modal.newLegFormModal.open(
                            firstSelectedEntity.departureID,
                            firstSelectedEntity.destinationID,
                            callback
                        );
                    }
                    else {
                        api.modal.newLegFormModal.open();
                    }
                }}
            >
                <span>New...</span>
                <span style={{ float: 'right' }}>
                    <Icon type="plus" />
                </span>
            </Menu.Item>
            <Divider type="horizontal" style={{ margin: 0 }} />
            {api.data.getLegs().map((leg) => {
                return (
                    <Menu.Item
                        key={`${leg.departure._id}::${leg.destination._id}`}
                        onClick={() => api.data.setEntityLegs(
                            api.selection.entityIds.get(),
                            leg.departure,
                            leg.destination)
                        }
                    >
                        {leg.departure.name + ' → ' + leg.destination.name}
                    </Menu.Item>
                )
            })}
        </Menu.SubMenu>
    }

    return (
        <Row type="flex" align="middle" className={BASE_CLS + '-toolbar'}>
            {editMode ? (
                <>
                    {renderFeature('collapseButton')(
                        <>
                            <Col>
                                <Button icon={collapserIcon} onClick={hideCarrierPanel.toggle} title={collapserTooltipText} />
                            </Col>
                            <Divider type="vertical" />
                        </>
                    )}
                    {renderFeature('editMenu')(
                        <Col>
                            <Dropdown
                                trigger={['click']}
                                overlay={
                                    <Menu /*onClick={handleAction}*/>
                                        <Menu.Item
                                            disabled={!selectedEntityIds.get().length}
                                            key="remove"
                                            onClick={() => api.data.removeEntities(selectedEntityIds.get())}
                                        >Remove</Menu.Item>
                                        {renderMoveToMenu()}
                                        <Menu.Item
                                            key="pilotNoteEditor"
                                            onClick={api.notesForPilot.editor.open}
                                        >
                                            Edit Pilot Notes
                                        </Menu.Item>
                                        <Menu.Item
                                            onClick={() => {
                                                const legs = data.getLegs();
                                                for (const leg of legs) {
                                                    const entityIds = leg.all.map(e => getSNodeID(api.toScheduleNode(e)));
                                                    api.data.setEntityLegs(
                                                        entityIds,
                                                        leg.destination,
                                                        leg.departure
                                                    );
                                                }
                                            }}
                                        >
                                            Swap all departures and destinations
                                        </Menu.Item>
                                        <Menu.Item
                                            disabled={scheduledPax.findIndex(
                                                (pax) => (
                                                    api.forms.entityForm.get(
                                                        getSNodeID(api.toScheduleNode(pax))
                                                    )?.chargeCode?.value
                                                )) === -1
                                            }
                                            key="clearChargeCodes"
                                            onClick={chargeCodes.clear}
                                        >Clear Charge Codes</Menu.Item>
                                        <Menu.Item
                                            title="Resets all modifications to pax/cgo"
                                            onClick={() => {
                                                Modal.confirm({
                                                    title: 'Are you sure you want to undo all changes?',
                                                    okText: 'Undo Everything',
                                                    okButtonProps: {
                                                        type: 'danger'
                                                    },
                                                    onOk: () => {
                                                        api.data.reset();
                                                    }
                                                })
                                            }}
                                        >
                                            Undo all changes
                                        </Menu.Item>
                                    </Menu>
                                }
                            >
                                <Button>
                                    Edit <Icon type="down" />
                                </Button>
                            </Dropdown>
                        </Col>
                    )}
                </>
            ) : null}
            {getFeatureConfig('selectMenu')?.forceVisible || editMode ?
                renderFeature('selectMenu')(<Col><SelectMenu api={api} /></Col>) : null
            }
            {renderFeature('viewMenu')(
                <Col>
                    <Dropdown
                        trigger={['click']}
                        overlay={
                            <Menu /*onClick={handleAction}*/>
                                <Menu.Item
                                    key="selectall"
                                    onClick={api.editor.visibleLegs.showAll}
                                >Expand All Tables</Menu.Item>
                                <Menu.Item
                                    key="deselectall"
                                    onClick={api.editor.visibleLegs.hideAll}
                                >Collapse All Tables</Menu.Item>
                            </Menu>
                        }
                    >
                        <Button>
                            View <Icon type="down" />
                        </Button>
                    </Dropdown>
                </Col>
            )}
            {renderFeature('toolsMenu')(<ToolsMenu api={api} />)}
            {editMode ? (
                renderFeature('pilotNodeEditorButton')(
                    <Col>
                        <Button
                            onClick={api.notesForPilot.editor.open}
                        >
                            Pilot Note Editor
                        </Button>
                    </Col>
                )
            ) : null}
            {editMode ? (
                renderFeature('defaultChargeCode')(
                    <Col style={{ marginLeft: 'auto' }}>
                        <span>Default Charge Code: </span>
                        <Input
                            style={{ width: 'auto', marginLeft: '0.25rem' }}
                            onChange={e => chargeCodes.setDefault(e.target.value.split(" ").join(""))}
                            value={chargeCodes.getDefault()}
                        />
                    </Col>
                )
            ) : null}
            {!editMode ? (
                renderFeature('editButton')(
                    userGroups.includes('flytsuite.schedule.passenger') ? (
                        <Col>
                            <Button
                                disabled={api.bulkIsnChecker.running}
                                type="primary"
                                onClick={api.editMode.toggle}
                            >
                                <Icon type="edit" style={{ marginRight: 6 }} />Edit
                            </Button>
                        </Col>
                    ) : (
                        <Tooltip title="You don't have permission to edit schedules">
                            <Col>
                                <Button disabled><Icon type="edit" style={{ marginRight: 6 }} />Edit</Button>
                            </Col>
                        </Tooltip>
                    )
                )
            ) : null}
            <div style={{ flex: 1 }} />
            {renderFeature('selectionCount')(
                selectedEntityIds.get().length ? (
                    <>
                        <Col>
                            <SelectionCount onClear={selectedEntityIds.clear} items={selectedEntityIds.get()} />
                        </Col>
                    </>
                ) : null
            )}
        </Row>
    )
}

const enabled: BaseFeatureConfig = { enabled: true }

export const DefaultFeatureConfig = {
    collapseButton: enabled,
    editMenu: enabled,
    selectMenu: enabled,
    viewMenu: enabled,
    toolsMenu: enabled,
    pilotNodeEditorButton: enabled,
    defaultChargeCode: enabled,
    editButton: enabled,
    selectionCount: enabled
} as const;

Toolbar.defaultProps = {
    featureConfig: DefaultFeatureConfig
}

export default Toolbar;